import { CONCEPT_STICKERS } from "./heroStickers";
import { buildTagStickers, StickerDef } from "./StickerDesigns";
import { classifyTag } from "./stickerClassifier";

// ─── "Lost in the Slush" flavor tags ──────────────────────────────────────
//
// The canonical source of truth for this list is the backend metadata map in
//   ecliptic-app/backend/mctwist-users-server/mctwist_users/resources/metadata.py
// where it appears under every sport/feature's `flavor → "Lost in the Slush"`
// category (the list is identical across all of them).
//
// We mirror it as a frontend constant because the Story Studio "General" sticker
// row shows the WHOLE flavor set regardless of the clip's sport/feature — so a
// per-clip backend fetch would be both wasteful and wrong (it would scope the
// list to the clip). Keep this in sync with metadata.py if the flavor set
// changes. Order is preserved from the backend.

export const LOST_IN_THE_SLUSH_TAGS: string[] = [
    "First Time", "#ImLearning", "#2ToMakeItTrue",
    "#SeeTheEdit", "#NoPoles", "#AllMyHomiesHateShaneFromLasVegasNevada", "#idFamous", "#SuperUnknown", "Beer in Hand",
    "Hotlaps", "Parkasaurus", "Urban", "Tree", "#Plug2Podium", "#KindaMid",
    "#ForTheCulture", "ILOVESNOWBOARDING", "Steezus Christ",
    "Down In the Golden Corral", "Cooked", "#Hooray", "#ParkCrewHatesYou", "Tweaked", "#420",
    "Skian On Them", "Cherry Peak Tap In", "Conway West", "Tubular",
    "God Save the Steeze", "Coach Kyle", "#XGameMode", "#HypeAF", "Dew Tour'd Le France",
    "Wear A Helmet", "Cigarette Puffin", "Cork Elementary School",
    "Factual Selection", "Side Hit Assasin", "Free Solo",
    "Cork Jesus", "#JustTheTip",
    "Hot Tub Time Machine", "Wu Tang Is For The Children",
    "Budget Ballin", "ILOVERAMEN", "S.L.C Punk", "Level2Productions",
    "A Bomb Hole", "Beaver Slappin", "Yard Sale", "Ball of Steeze",
    "You Gotta Go Pro You Gotta Go", "Goblin Mode",
    "Gremlin Mind", "Demon Time", "G-LOC", "10Gs", "Centrifugal", "Mach 10",
    "Hypersonic", "We're So Back", "I'm Bing Chillin'", "One of Those Days",
    "Afterbang", "Afterbang Banger", "Afterbang Overload",
    "idX Heater", "Content 🥸", "Slytherin", "Meat Vessel", "Bad Larry",
    "Spooky Szn", "360HP", "Follow Sham", "Penjamin Riplin",
    "Clown School Graduate", "The Biskness", "Big Biskness", "Just Biskness",
    "Merry Biskmas", "#CorkYourGrabs", "#GrabYourCorks", "Penjamin City", "#FISSucks",
    "Battle of Mid", "Midland Warriors", "Heartbreak Midfare", "Infinite Degrees",
    "Big Mid Energy", "Dead Reckoning", "We Want Graph 📊", "Graph Revenge Tour 📈",
    "Mathilde Graphmaud", "Max Graphatt", "Evan McGraphran", "S.M.U.S.H",
    "Phil Casagraph", "Andri Ragraphtli", "Ayumu Hiragraph", "Yuto Tographuka",
    "Graphleen Gu", "Tess LeGraph", "Maddie Graphstro", "Lucas Graphster",
    "Alex Ferrgrapha", "Tucker GraphSimons", "Liam Graphley", "Colby Graphson",
    "Strictly Graphness", "The Graph Project",
    "Zoe Send-It-To-The-House Synnott", "Shaun Black",
    "Mark McTwist", "Henrik Dawglaut", "Mia Cooks", "Rene Lipekangas",
    "Hunter Hess Shreds My Pipe", "Alex Hall Pass", "Tanner Hall Pass 🤔",
    "Hot Dog Franzia", "Art", "Locally Developed", "Big Unc", "Llama Patrol",
    "855-MAX-BISK", "TOP SECRET",
];

// Stable hash so each tag maps to a consistent (but varied) variant — not random,
// so the grid looks the same across renders.
function hashTag(s: string): number {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
    return Math.abs(h);
}

// Spray + comic-pop look great on SHORT punchy tags but get muddy once a long
// label wraps to 3 lines; burst cramps past ~3 words. So variant choice is
// length-aware: the longer the tag, the more we restrict to the variants that
// stay crisp at any length (the plate + the banner).
const MEDIUM_VARIANTS = new Set(["neon-pill", "ribbon", "burst-badge"]);
const LONG_VARIANTS = new Set(["neon-pill", "ribbon"]);
// Very long labels (4+ words) only stay crisp on the plate.
const VERY_LONG_VARIANTS = new Set(["neon-pill"]);

// Build exactly one sticker per General tag. A hand-tuned hero design wins when
// the tag has one (e.g. "Beer in Hand", "Parkasaurus"); otherwise the tag is
// spread deterministically across the (length-appropriate) generic variants so
// the grid reads as varied rather than ~130 identical pills. Every returned def
// carries `label` so the General search box can filter by tag text.
export function buildLostInTheSlushStickers(): StickerDef[] {
    return LOST_IN_THE_SLUSH_TAGS.map((tag) => {
        const clean = tag.trim();
        const all = buildTagStickers(tag, classifyTag(tag));
        if (CONCEPT_STICKERS[clean.toLowerCase()]) return { ...all[0], label: tag };
        const words = clean.split(/\s+/).length;
        const allow =
            words >= 4 ? VERY_LONG_VARIANTS
                : clean.length > 20 ? LONG_VARIANTS
                    : clean.length > 13 || words >= 3 ? MEDIUM_VARIANTS
                        : null;
        const pool = allow ? all.filter((d) => allow.has(d.variant)) : all;
        const chosen = (pool.length ? pool : all)[hashTag(tag) % (pool.length || all.length)];
        return { ...chosen, label: tag };
    });
}
